import { Button, Flex, Space } from "../ui";
import React from "react";

/**
 * Rolle eines Bausteins für die Aktions- und Breitenlogik der Liste. Antd-`Button`, `Space` und `Flex`
 * werden direkt erkannt; Produkte mit eigenen Knopf- oder Abstandsbausteinen kennzeichnen diese über
 * die statische Eigenschaft `settingsListRole` ("button" bzw. "group"), damit Kompaktdarstellung
 * (nur Symbol), Zählung der Aktionen und Spaltenbreiten für sie genauso funktionieren.
 */
export type SettingsListElementRole = "button" | "group" | "render";
const ROLE_BUTTON = "button";
const ROLE_GROUP = "group";
const ROLE_RENDER = "render";

function declaredRole(type: unknown): SettingsListElementRole | undefined {
  if (type == null || (typeof type !== "object" && typeof type !== "function")) return undefined;
  const role = (type as { settingsListRole?: unknown }).settingsListRole;
  return role === ROLE_BUTTON || role === ROLE_GROUP || role === ROLE_RENDER ? role : undefined;
}

/**
 * Reine Darstellungsbausteine (`settingsListRole = "render"`, z. B. beschriftete Zeilenaktionen) werden
 * aufgelöst, damit Aktionsmessung und Nur-Symbol-Darstellung die enthaltenen Knöpfe sehen. Die Kindform
 * bleibt erhalten (Tooltip/Dropdown erwarten genau ein Element).
 */
export function expandSettingsListRenderers(node: React.ReactNode): React.ReactNode {
  if (Array.isArray(node)) return node.map(expandSettingsListRenderers);
  if (!React.isValidElement(node)) return node;
  if (typeof node.type === "function" && declaredRole(node.type) === ROLE_RENDER) {
    return expandSettingsListRenderers((node.type as (props: unknown) => React.ReactNode)(node.props));
  }
  const element = node as React.ReactElement<{ children?: React.ReactNode }>;
  if (element.props.children == null) return node;
  return React.cloneElement(element, {}, expandSettingsListRenderers(element.props.children));
}

export function isSettingsListButtonElement(el: React.ReactElement): boolean {
  return el.type === Button || declaredRole(el.type) === ROLE_BUTTON;
}

export function isSettingsListGroupElement(el: React.ReactElement): boolean {
  return el.type === Space || el.type === Flex || declaredRole(el.type) === ROLE_GROUP;
}
